import React, { useState } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  message,
  Typography,
  Spin,
  GetProp,
  Col,
  Row,
  Alert,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import {
  login,
  setAuthToken,
  forgotPassword,
  resetPassword,
  loginWithGoogle,
} from "./service/service";
import Header from "./Header";
import hourGlassIcon from "./icons/hour-glass.gif";
import {
  ArrowRightOutlined,
  CloseSquareFilled,
  RestTwoTone,
} from "@ant-design/icons";
import "./Login.css";
import { OTPProps } from "antd/es/input/OTP";
import form from "antd/es/form";
import FacebookLogin from "react-facebook-login";
import {
  CredentialResponse,
  GoogleLogin,
  GoogleOAuthProvider,
} from "@react-oauth/google";

const { Text } = Typography;

const LoginModal = ({}: {}) => {
  const { isLoggedIn, setToken, user, logout, agentInstance } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [passwordValidation, setPasswordValidation] = useState({
    hasSpecialCharacters: false,
    hasAlphaNumeric: false,
    hasUpperCase: false,
    isSixLettersLong: false,
    isNewConfirmMatch: false,
  });
  const [forgotEmail, setForgotEmail] = useState("");

  const clientId =
    "1067434998710-8sgg3526t7otsbcnjf89uppkvepfocgd.apps.googleusercontent.com";

  const [form] = Form.useForm();
  const navigate = useNavigate();

  const loginFlow = async (access_token: string | null) => {
    if (access_token) {
      localStorage.setItem("token", access_token);
      setAuthToken(access_token);
      await setToken(access_token);
      if (agentInstance) navigate("/dashboard");
      else navigate("/settings");
    } else {
      message.error(
        "Unable to authenticate user. ( failed to get access token ). Please contact system administrator"
      );
    }
  };

  const handleLogin = async (values: { email: string; password: string }) => {
    try {
      setLoading(true);
      const response = await login(values.email, values.password);
      await loginFlow(response.data.access_token);
    } catch (error: any) {
      form.setFieldsValue({ password: "" });
      const passwordField = form.getFieldInstance("password");
      if (passwordField) {
        passwordField.focus();
      }
      const errorMessage =
        error.response?.data?.exception?.detail ||
        "An error occurred. Please try again later.";
      message.error(errorMessage, 10);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (values: { email: string }) => {
    try {
      setLoading(true);
      const response = await forgotPassword(values.email);
      setForgotEmail(values.email);
      setResetToken(response.data.reset_token);
    } catch (error: any) {
    } finally {
      message.success(
        "If you have account with us, you will receive an email with instructions to reset your password."
      );
      setShowForgotPassword(false);
      setShowNewPasswordForm(true);
      setResetToken("");
      setLoading(false);
    }
  };

  const handleResetPassword = async (values: {
    resetToken: string;
    newPassword: string;
  }) => {
    try {
      setLoading(true);
      await resetPassword(forgotEmail, values.newPassword, values.resetToken);
      message.success("Password reset successful.");
      setShowNewPasswordForm(false);
      setShowForgotPassword(false);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.exception?.message ||
        "An error occurred. Please try again later.";
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const showError = (errorMessage: string) => {
    message.error(errorMessage, 10);
  };

  const handleContinue = () => {
    navigate("/dashboard");
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPasswordValidation({
      hasSpecialCharacters: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
      hasAlphaNumeric: /^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]+)$/.test(
        newPassword
      ),
      hasUpperCase: /[A-Z]/.test(newPassword),
      isSixLettersLong: newPassword.length >= 6,
      isNewConfirmMatch: newPassword === form.getFieldValue("confirmPassword"),
    });
  };

  const responseFacebook = (response: any) => {
    console.log(response);
    // Handle Facebook login response
  };

  const responseGoogle = (response: any) => {
    console.log(response);
    // Handle Facebook login response
  };

  const handleLoginWithGoogleSuccess = async (
    credentialResponse: CredentialResponse
  ) => {
    if (credentialResponse.credential) {
      try {
        const response = await loginWithGoogle(
          credentialResponse.credential,
          clientId,
          "btn"
        );
        await loginFlow(response.access_token);
      } catch (error: any) {
        console.log(error);
        const errorMessage =
          error.response?.data?.exception?.detail ||
          "An error occurred. Please try again later.";
        message.error(errorMessage, 10);
      }
    } else {
      message.error(
        "Unable to login with Google ( Credential is missing in the response )",
        10
      );
    }
  };

  return (
    <Row>
      <Col span={20} offset={2}>
        {isLoggedIn ? (
          <div className="welcome-back">
            <img className="welcome-icon" src={hourGlassIcon} alt="Home" />
            <p className="welcome-message">
              Welcome back, <span className="user-name">{user?.full_name}</span>
              !
            </p>
            <div>
              <Button
                type="primary"
                onClick={handleContinue}
                icon={<ArrowRightOutlined />}
                style={{ borderRadius: "0px" }}
              >
                Continue
              </Button>

              <Button onClick={logout} style={{ borderRadius: "0px" }}>
                Log Off
              </Button>
            </div>
          </div>
        ) : (
          <div>
            {showForgotPassword ? (
              <ForgotPasswordForm
                onSubmit={handleForgotPassword}
                onCancel={() => setShowForgotPassword(false)}
                loading={loading}
              />
            ) : showNewPasswordForm ? (
              <NewPasswordForm
                onSubmit={handleResetPassword}
                onCancel={() => setShowNewPasswordForm(false)}
                loading={loading}
              />
            ) : (
              <div>
                <LoginForm
                  form={form}
                  loading={loading}
                  onForgotPassword={() => setShowForgotPassword(true)}
                  onSubmit={handleLogin}
                />
                {/* <FacebookLogin
                  appId="1024417872388999"
                  autoLoad={false}
                  fields="name,email,picture"
                  onClick={() => setLoading(true)}
                  callback={responseFacebook}
                  onFailure={() => setLoading(false)}
                  textButton={loading ? "Loading..." : "Login with Facebook"}
                /> */}
                <Row>
                  <Col xs={{ span: 8, offset: 4 }} lg={{ span: 8, offset: 4 }}>
                    {/* <GoogleLogin
                      onSuccess={handleLoginWithGoogleSuccess}
                      onError={() => {
                        console.log("Login Failed");
                      }}
                    /> */}
                  </Col>
                </Row>
              </div>
            )}
          </div>
        )}
      </Col>
    </Row>
  );
};

interface LoginFormProps {
  form: any;
  loading: boolean;
  onForgotPassword: () => void;
  onSubmit: (values: { email: string; password: string }) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  form,
  loading,
  onForgotPassword,
  onSubmit,
}) => {
  const handleEmailBlur = () => {
    const value = form.getFieldValue("email");
    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      form.setFields([
        {
          name: "email",
          errors: ["Invalid email format"],
        },
      ]);
    } else {
      form.setFields([
        {
          name: "email",
          errors: [],
        },
      ]);
    }
  };

  const handleSubmit = (values: any) => {
    onSubmit(values);
  };

  const handlePasswordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      form.submit();
    }
  };

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 12 },
  };

  return (
    <Form form={form} onFinish={handleSubmit} {...layout}>
      <Form.Item
        label="Email"
        name="email"
        rules={[{ required: true, message: "Please input your email!" }]}
      >
        <Input
          placeholder="Please enter email address"
          tabIndex={1}
          onBlur={handleEmailBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              form.setFieldsValue({ password: "" });
              form.validateFields(["password"]);
              form.getFieldInstance("password").focus();
            }
          }}
        />
      </Form.Item>
      <Form.Item
        label="Password"
        name="password"
        rules={[{ required: true, message: "Please input your password!" }]}
      >
        <Input.Password
          placeholder="Please enter your password"
          tabIndex={2}
          onKeyDown={handlePasswordKeyDown}
        />
      </Form.Item>

      <Form.Item wrapperCol={{ offset: 6, span: 8 }}>
        <Row>
          <Col span={6}>
            <Button
              type="primary"
              htmlType="submit"
              className="login-button"
              loading={loading}
              tabIndex={3}
              style={{ width: "100%" }}
            >
              {loading ? <Spin /> : "Log in"}
            </Button>
          </Col>
          <Col span={6}>
            <Button
              type="link"
              onClick={onForgotPassword}
              className="forgot-password-button"
              tabIndex={4}
              style={{ width: "100%", textAlign: "right" }}
            >
              Forgot Password?
            </Button>
          </Col>
        </Row>
      </Form.Item>
    </Form>
  );
};

interface ForgotPasswordFormProps {
  onSubmit: (values: { email: string }) => void;
  onCancel: () => void;
  loading: boolean;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onSubmit,
  onCancel,
  loading,
}) => {
  const handleSubmit = (values: any) => {
    onSubmit(values);
  };

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 10 },
  };

  return (
    <Form onFinish={handleSubmit} {...layout}>
      <Form.Item
        label="Email"
        name="email"
        rules={[{ required: true, message: "Please input your email!" }]}
      >
        <Input placeholder="Please enter your email" />
      </Form.Item>

      <Row>
        <Col xs={{ span: 4, offset: 1 }} lg={{ span: 3, offset: 2 }}></Col>
        <Col xs={{ span: 6, offset: 4 }} lg={{ span: 4, offset: 4 }}>
          <Button type="primary" htmlType="submit" loading={loading}>
            Reset Password
          </Button>
        </Col>
        <Col xs={{ span: 5, offset: 1 }} lg={{ span: 6, offset: 1 }}>
          <Button type="link" onClick={onCancel}>
            Cancel
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

interface NewPasswordFormProps {
  onSubmit: (values: { resetToken: string; newPassword: string }) => void;
  onCancel: () => void;
  loading: boolean;
}

const NewPasswordForm: React.FC<NewPasswordFormProps> = ({
  onSubmit,
  onCancel,
  loading,
}) => {
  const [resetToken, setResetToken] = useState("");
  const [passwordValidation, setPasswordValidation] = useState({
    hasSpecialCharacters: false,
    hasAlphaNumeric: false,
    hasUpperCase: false,
    isSixLettersLong: false,
  });

  const handleSubmit = (values: any) => {
    onSubmit({
      resetToken: values.resetToken,
      newPassword: values.newPassword,
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPasswordValidation({
      hasSpecialCharacters: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
      hasAlphaNumeric: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]/.test(newPassword),
      hasUpperCase: /[A-Z]/.test(newPassword),
      isSixLettersLong: newPassword.length >= 6,
    });
  };

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 12 },
  };

  return (
    <Form onFinish={handleSubmit} {...layout}>
      <Form.Item
        label="Passcode"
        name="resetToken"
        initialValue={resetToken}
        rules={[{ required: true, message: "Please input your passcode!" }]}
      >
        <Input
          onChange={(e) => setResetToken(e.target.value)}
          placeholder="Please enter passcode"
        />
      </Form.Item>
      <Form.Item
        label="New Password"
        name="newPassword"
        rules={[{ required: true, message: "Please input your new password!" }]}
      >
        <Input.Password
          placeholder="Please enter your new password"
          onChange={handlePasswordChange}
        />
      </Form.Item>
      <Row>
        <ul>
          <li>
            {passwordValidation.hasSpecialCharacters ? (
              <Text type="success">Must have special characters</Text>
            ) : (
              <Text type="danger">Must have special characters</Text>
            )}
          </li>
          <li>
            {passwordValidation.hasAlphaNumeric ? (
              <Text type="success">Must have alphanumeric</Text>
            ) : (
              <Text type="danger">Must have alphanumeric</Text>
            )}
          </li>
          <li>
            {passwordValidation.hasUpperCase ? (
              <Text type="success">Must have uppercase letter</Text>
            ) : (
              <Text type="danger">Must have uppercase letter</Text>
            )}
          </li>
          <li>
            {passwordValidation.isSixLettersLong ? (
              <Text type="success">Must be 6 letters long</Text>
            ) : (
              <Text type="danger">Must be 6 letters long</Text>
            )}
          </li>
        </ul>
      </Row>
      <Form.Item wrapperCol={{ offset: 12, span: 2 }}>
        <Row>
          <Col span={4}>
            <Button type="primary" htmlType="submit" loading={loading}>
              Reset Password
            </Button>
          </Col>
          <Col span={12}>
            <Button type="link" onClick={onCancel}>
              Cancel
            </Button>
          </Col>
        </Row>
      </Form.Item>
    </Form>

    // <Form onFinish={handleSubmit}>
    //   <Alert
    //     banner
    //     message={`If an account with your email address exists, a reset passcode will be
    //   emailed to you, please check your inbox.`}
    //     closable={{
    //       "aria-label": "close",
    //       closeIcon: <CloseSquareFilled style={{ color: "white" }} />,
    //     }}
    //     type="warning"
    //   />

    //   <Form.Item
    //     label="Passcode"
    //     name="resetToken"
    //     initialValue={resetToken}
    //     rules={[{ required: true, message: "Please input your passcode!" }]}
    //   >
    //     <Input
    //       onChange={(e) => setResetToken(e.target.value)}
    //       placeholder="Please enter passcode"
    //     />
    //   </Form.Item>
    //   <Form.Item
    //     label="New Password"
    //     name="newPassword"
    //     rules={[{ required: true, message: "Please input your new password!" }]}
    //   >
    //     <Input.Password
    //       placeholder="Please enter your new password"
    //       onChange={handlePasswordChange}
    //     />
    //   </Form.Item>
    //   <div style={{ marginTop: "10px" }}>
    //     <Text type="secondary">Password Requirements:</Text>
    //     <ul>
    //       <li>
    //         {passwordValidation.hasSpecialCharacters ? (
    //           <Text type="success">Must have special characters</Text>
    //         ) : (
    //           <Text type="danger">Must have special characters</Text>
    //         )}
    //       </li>
    //       <li>
    //         {passwordValidation.hasAlphaNumeric ? (
    //           <Text type="success">Must have alphanumeric</Text>
    //         ) : (
    //           <Text type="danger">Must have alphanumeric</Text>
    //         )}
    //       </li>
    //       <li>
    //         {passwordValidation.hasUpperCase ? (
    //           <Text type="success">Must have uppercase letter</Text>
    //         ) : (
    //           <Text type="danger">Must have uppercase letter</Text>
    //         )}
    //       </li>
    //       <li>
    //         {passwordValidation.isSixLettersLong ? (
    //           <Text type="success">Must be 6 letters long</Text>
    //         ) : (
    //           <Text type="danger">Must be 6 letters long</Text>
    //         )}
    //       </li>
    //     </ul>
    //   </div>
    //   <Form.Item>
    //     <Button type="primary" htmlType="submit" loading={loading}>
    //       Reset Password
    //     </Button>
    //     <Button type="link" onClick={onCancel}>
    //       Cancel
    //     </Button>
    //   </Form.Item>
    // </Form>
  );
};

export default LoginModal;
